import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import fetcher, { loadingStatus } from 'helpers/fetcher';
import { fetchPage } from 'store/slices/pageSlice';
import Icon from 'components/Icon';

const DEFAULT_VACANCIES = [
	{
		title: 'Ученик обвальщика без опыта (учебный период 2 месяца, с последующим трудоустройством)',
		salary: 'ЗП: на период обучения 40-50 тыс.',
		extra: '',
	},
	{ title: 'Обвальщик мяса с опытом работы', salary: 'ЗП: до 150 тыс.', extra: '' },
	{ title: 'Жиловщица мяса', salary: 'ЗП: от 100 тыс.', extra: '' },
	{ title: 'Водитель', salary: 'ЗП: 60 тыс.', extra: '' },
	{ title: 'Работник в цех рубленных полуфабрикатов', salary: 'ЗП: от 60 тыс.', extra: '' },
	{ title: 'Продавец в фирменный магазин', salary: 'ЗП: от 50 тыс.', extra: '' },
	{ title: 'Экономист', salary: 'ЗП: от 60 тыс.', extra: '' },
	{ title: 'Повар в магазин', salary: 'ЗП: от 3 тыс. за смену', extra: '' },
	{ title: 'Мастер чистоты', salary: 'ЗП: 45 тыс.', extra: '' },
];

const PageVacancies = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { data, status } = useSelector(state => state.page.page);
	const mainInfo = useSelector(state => state.layout.mainInfo.data);
	const [modalVacancy, setModalVacancy] = useState(null);
	const [vacancies, setVacancies] = useState(DEFAULT_VACANCIES);
	const [commonText, setCommonText] = useState(t('vacancies.defaultCommonText'));

	useEffect(() => {
		const vacanciesId = mainInfo?.top_menu?.vacancies?.id;
		if (vacanciesId) {
			dispatch(fetchPage({ id: vacanciesId }));
		}
	}, [dispatch, mainInfo]);

	useEffect(() => {
		fetcher('/index.php?route=information/vacancies')
			.then(res => (res && res.ok ? res.json() : null))
			.then(data => {
				if (!data) return;
				if (data.commonText) setCommonText(data.commonText);
				if (Array.isArray(data.vacancies) && data.vacancies.length > 0) {
					setVacancies(
						data.vacancies.map(v => ({
							title: v.title || '',
							salary: v.salary || '',
							extra: v.extra || '',
						}))
					);
				}
			})
			.catch(() => {});
	}, []);

	const benefits = [
		{
			icon: 'icon-benefit-comfort',
			title: t('vacancies.benefitComfortTitle'),
			text: t('vacancies.benefitComfortText'),
		},
		{
			icon: 'icon-benefit-rest',
			title: t('vacancies.benefitRestTitle'),
			text: t('vacancies.benefitRestText'),
		},
		{
			icon: 'icon-benefit-adventure',
			title: t('vacancies.benefitAdventureTitle'),
			text: t('vacancies.benefitAdventureText'),
		},
	];

	return (
		<>
			<div className='region vacancies-section'>
				{/* Breadcrumb */}
				<div className='contact-breadcrumb' style={{ marginTop: '64px' }}>
					<a
						href='/'
						onClick={e => {
							e.preventDefault();
							navigate('/');
						}}
						className='breadcrumb-link'
					>
						{t('common.home')}
					</a>
					<span className='breadcrumb-separator breadcrumb-separator-active'></span>
					<span className='breadcrumb-current'>{t('vacancies.breadcrumb')}</span>
				</div>

				{/* Title */}
				<h1 className='vacancies-title'>{t('vacancies.title')}</h1>

				{status === loadingStatus.SUCCEEDED && data ? (
					<>
						{/* Main content */}
						<div className='vacancies-content'>
							<div className='vacancies-description'>
								<p>{t('vacancies.companyIntro1')}</p>
								<p>{t('vacancies.companyIntro2')}</p>
								<p>{t('vacancies.companyIntro3')}</p>
							</div>
							<div className='vacancies-contacts'>
								<div className='vacancies-contacts-item'>
									<h4 className='vacancies-contacts-label'>
										{t('vacancies.phoneLabel')}
									</h4>
									<a
										href='tel:+78662777880'
										className='vacancies-contacts-value'
									>
										+7 (8662) 77-78-80
									</a>
								</div>
								<div className='vacancies-contacts-item'>
									<h4 className='vacancies-contacts-label'>{t('vacancies.emailLabel')}</h4>
									<a
										href='mailto:rayan07@bk.ru'
										className='vacancies-contacts-value'
									>
										rayan07@bk.ru
									</a>
								</div>
							</div>
						</div>

						{/* Benefits */}
						<div className='vacancies-benefits'>
							{benefits.map((benefit, index) => (
								<div key={index} className='vacancies-benefit-card'>
									<Icon
										name={benefit.icon}
										className='vacancies-benefit-icon'
									/>
									<h3 className='vacancies-benefit-title'>{benefit.title}</h3>
									<p className='vacancies-benefit-text'>{benefit.text}</p>
								</div>
							))}
						</div>

						{/* Vacancies list */}
						<div className='vacancies-list-section'>
							<h2 className='vacancies-list-title'>{t('vacancies.allVacancies')}</h2>
							<div className='vacancies-grid'>
								{vacancies.map((vacancy, index) => (
									<button
										key={index}
										type='button'
										className='vacancy-button'
										onClick={() => setModalVacancy(vacancy)}
									>
										{vacancy.title}
									</button>
								))}
							</div>
						</div>
					</>
				) : status === loadingStatus.LOADING ? (
					<>
						<Skeleton
							active
							paragraph={{ rows: 4 }}
							style={{ marginBottom: '48px' }}
						/>
						<Skeleton
							active
							paragraph={{ rows: 6 }}
							style={{ marginBottom: '48px' }}
						/>
					</>
				) : (
					<div>{t('common.pageNotFound')}</div>
				)}
			</div>

			<Modal
				open={!!modalVacancy}
				title={modalVacancy?.title}
				onCancel={() => setModalVacancy(null)}
				footer={null}
				width={560}
			>
				{modalVacancy && (
					<div style={{ whiteSpace: 'pre-line', color: '#333' }}>
						<p style={{ marginBottom: 16, fontWeight: 600 }}>
							{modalVacancy.salary}
						</p>
						{modalVacancy.extra && (
							<p style={{ marginBottom: 16 }}>{modalVacancy.extra}</p>
						)}
						{commonText}
					</div>
				)}
			</Modal>

			{/* Quality Section */}
			{status === loadingStatus.SUCCEEDED && data && (
				<div
					className='region news-quality-section'
					style={{ marginBottom: '60px' }}
				>
				<h2 className='quality-title'>{t('quality.title')}</h2>
				<div className='quality-cards'>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-halal.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>
							{t('quality.noPork')}
						</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-eggs.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.naturalEggs')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-cow.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.ownSlaughter')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-nature.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>
							{t('quality.noPreservatives')}
						</h4>
					</div>
					<div
						className='quality-card quality-card-blue'
						style={{
							backgroundImage: `url(${process.env.PUBLIC_URL || ''}/images/bg-quality-card.webp)`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					>
						<Icon name='icon-dna' className='quality-card-icon' />
						<h4 className='quality-card-text quality-card-text-white'>
							{t('quality.noGMO')}
						</h4>
					</div>
				</div>
				</div>
			)}
		</>
	);
};

export default PageVacancies;
